/**
 * createT — fabrique de traducteurs par chemin, partagée par les hooks de
 * page (`useHomeV2T`, `usePlansT`, `useTeamT`, `useHowItWorksT`, …).
 *
 * Les quatre premiers hooks i18n du repo (`useHomeT`, `useEngineT`,
 * `useBlogT`, `useAttorneysT`) recopiaient chacun les mêmes trente lignes :
 * `lookup`, `interpolate`, et une résolution de langue légèrement
 * différente à chaque fois. Quatre copies, quatre comportements de repli.
 * Ici, une seule.
 *
 * Repli : langue demandée → français → le chemin lui-même. Rendre le
 * chemin plutôt qu'une chaîne vide fait surgir la clé manquante à l'écran
 * pendant le développement, au lieu de laisser un trou silencieux en
 * production.
 *
 * Le français est le repli, pas l'anglais : les pages publiques sont
 * écrites en français (seul marché actif), et c'est le texte validé.
 */

const DEFAULT_LANG = 'fr';

function interpolate(template, params) {
  if (!params || typeof template !== 'string') return template;
  let out = template;
  for (const [k, v] of Object.entries(params)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

function lookup(tree, path) {
  if (!tree || !path) return undefined;
  const parts = path.split('.');
  let node = tree;
  for (const p of parts) {
    if (node && typeof node === 'object' && p in node) node = node[p];
    else return undefined;
  }
  return node;
}

/**
 * @param {object} dict  dictionnaire { fr: {...}, nl: {...}, en: {...} }
 * @param {string} language  'fr' | 'nl' | 'en' (tolère 'fr-BE', 'FR', …)
 * @returns {(path: string, params?: object) => any}
 *   Rend la valeur au chemin donné. Les valeurs non-chaîne (tableaux,
 *   objets) sont rendues telles quelles — c'est ce qui permet de garder
 *   des listes en JSON plutôt que de les recomposer côté JSX.
 */
export function createT(dict, language) {
  const short = String(language || DEFAULT_LANG).slice(0, 2).toLowerCase();
  const lang = dict[short] ? short : DEFAULT_LANG;
  return (path, params) => {
    const hit = lookup(dict[lang], path);
    const withFallback = hit === undefined ? lookup(dict[DEFAULT_LANG], path) : hit;
    const value = withFallback === undefined ? path : withFallback;
    return typeof value === 'string' ? interpolate(value, params) : value;
  };
}

export default createT;
